import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { TrendingUp, TrendingDown, Minus, Users, Building2 } from 'lucide-react';
import { AccumulationTrend } from '../../types/whale-tracking';

export function AccumulationTrends() {
  // Mock trends - will be replaced with real data
  const trends: AccumulationTrend[] = [
    {
      token: {
        address: '0x0',
        symbol: 'ETH',
        name: 'Ethereum',
        decimals: 18,
        price: 2245.50,
      },
      netFlow: '+45,230',
      whalesBuying: 23,
      whalesSelling: 8,
      exchangeNetFlow: '-$145.2M',
      trend: 'strong_accumulation',
      confidence: 89,
    },
    {
      token: {
        address: '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599',
        symbol: 'WBTC',
        name: 'Wrapped Bitcoin',
        decimals: 8,
        price: 43250,
      },
      netFlow: '+1,245',
      whalesBuying: 15,
      whalesSelling: 12,
      exchangeNetFlow: '-$23.4M',
      trend: 'accumulation',
      confidence: 72,
    },
    {
      token: {
        address: '0x1234567890123456789012345678901234567890',
        symbol: 'NDH',
        name: 'Nardium Token',
        decimals: 18,
        price: 0.85,
      },
      netFlow: '+2,500,000',
      whalesBuying: 8,
      whalesSelling: 2,
      exchangeNetFlow: '-$1.8M',
      trend: 'strong_accumulation',
      confidence: 94,
    },
    {
      token: {
        address: '0x514910771AF9Ca656af840dff83E8264EcF986CA',
        symbol: 'LINK',
        name: 'Chainlink',
        decimals: 18,
        price: 14.25,
      },
      netFlow: '+125,000',
      whalesBuying: 11,
      whalesSelling: 9,
      exchangeNetFlow: '+$2.1M',
      trend: 'neutral',
      confidence: 58,
    },
    {
      token: {
        address: '0x7Fc66500c84A76Ad7e9c93437bFc5Ac33E2DDaE9',
        symbol: 'AAVE',
        name: 'Aave',
        decimals: 18,
        price: 98.50,
      },
      netFlow: '-45,000',
      whalesBuying: 5,
      whalesSelling: 14,
      exchangeNetFlow: '+$8.5M',
      trend: 'distribution',
      confidence: 76,
    },
  ];

  const getTrendBadge = (trend: string) => {
    switch (trend) {
      case 'strong_accumulation':
        return (
          <Badge className="bg-green-500/20 text-green-500 border-green-500/30">
            <TrendingUp className="h-3 w-3 mr-1" />
            Acumulacion Fuerte
          </Badge>
        );
      case 'accumulation':
        return (
          <Badge className="bg-emerald-500/20 text-emerald-500 border-emerald-500/30">
            <TrendingUp className="h-3 w-3 mr-1" />
            Acumulacion
          </Badge>
        );
      case 'neutral':
        return (
          <Badge className="bg-slate-500/20 text-slate-500 border-slate-500/30">
            <Minus className="h-3 w-3 mr-1" />
            Neutral
          </Badge>
        );
      case 'distribution':
        return (
          <Badge className="bg-orange-500/20 text-orange-500 border-orange-500/30">
            <TrendingDown className="h-3 w-3 mr-1" />
            Distribucion
          </Badge>
        );
      case 'strong_distribution':
        return (
          <Badge className="bg-red-500/20 text-red-500 border-red-500/30">
            <TrendingDown className="h-3 w-3 mr-1" />
            Distribucion Fuerte
          </Badge>
        );
      default:
        return null;
    }
  };

  const isPositiveFlow = (flow: string) => flow.startsWith('+') || (!flow.startsWith('-') && !flow.startsWith('$'));
  const isNegativeExchangeFlow = (flow: string) => flow.startsWith('-');

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-green-500" />
            Tendencias de Acumulacion (24h)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            Analisis de movimientos de ballenas y flujo de exchanges para detectar tendencias de acumulacion o distribucion.
          </p>

          <div className="space-y-4">
            {trends.map((trend) => (
              <div
                key={trend.token.symbol}
                className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg"
              >
                {/* Token Info */}
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-violet-500 to-purple-500 flex items-center justify-center text-white font-bold">
                    {trend.token.symbol.charAt(0)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                        {trend.token.symbol}
                      </h3>
                      {getTrendBadge(trend.trend)}
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {trend.token.name}
                    </p>
                  </div>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-6">
                  {/* Net Flow */}
                  <div className="text-right">
                    <p className="text-xs text-slate-600 dark:text-slate-400">Net Flow</p>
                    <p className={`font-medium ${isPositiveFlow(trend.netFlow) ? 'text-green-500' : 'text-red-500'}`}>
                      {trend.netFlow} {trend.token.symbol}
                    </p>
                  </div>

                  {/* Whales Activity */}
                  <div className="text-center hidden md:block">
                    <p className="text-xs text-slate-600 dark:text-slate-400 flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      Whales
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="text-green-500 font-medium">{trend.whalesBuying}</span>
                      <span className="text-slate-400">/</span>
                      <span className="text-red-500 font-medium">{trend.whalesSelling}</span>
                    </div>
                  </div>

                  {/* Exchange Flow */}
                  <div className="text-right hidden md:block">
                    <p className="text-xs text-slate-600 dark:text-slate-400 flex items-center gap-1 justify-end">
                      <Building2 className="h-3 w-3" />
                      Exchange Flow
                    </p>
                    <p className={`font-medium ${isNegativeExchangeFlow(trend.exchangeNetFlow) ? 'text-green-500' : 'text-red-500'}`}>
                      {trend.exchangeNetFlow}
                    </p>
                  </div>

                  {/* Confidence */}
                  <div className="text-right">
                    <p className="text-xs text-slate-600 dark:text-slate-400">Confianza</p>
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            trend.confidence >= 80
                              ? 'bg-green-500'
                              : trend.confidence >= 60
                              ? 'bg-yellow-500'
                              : 'bg-red-500'
                          }`}
                          style={{ width: `${trend.confidence}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                        {trend.confidence}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-500/10 border-blue-500/20">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Building2 className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <h4 className="font-medium text-slate-900 dark:text-slate-100">
                Como interpretar el Exchange Flow
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                <strong className="text-green-500">Flujo negativo</strong> (retiros &gt; depositos) indica acumulacion - las ballenas estan sacando tokens de exchanges para holdear.
                <br />
                <strong className="text-red-500">Flujo positivo</strong> (depositos &gt; retiros) indica posible presion de venta - los tokens se mueven a exchanges para vender.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
